import { useFieldArray, useWatch, UseFormReturn } from 'react-hook-form';
import { Button } from '@/components/ui/button.js';
import { FormInputTime } from '@/components/forms/form-input-time.js';
import { FormInputMobileTimeRangeSheet } from '@/components/forms/components/form-input-mobile-time-range-sheet.js';
import { InfoKeyValue } from '@/components/info-key-value.js';
import { formatPlainTime } from '@airreading/common';
import { CirclePlus, CircleX } from 'lucide-react';
import { Temporal } from 'temporal-polyfill';
import { CoachAvailabilityFormState } from '@/lib/coach-availability-utils.js';
import { DayOfWeek, DayOfWeekLabelMap } from '@airreading/common/data';
import { ReactNode, useEffect, useState } from 'react';

export interface WeeklyHoursRowProps {
  form: UseFormReturn<CoachAvailabilityFormState>;
  day: DayOfWeek;
  dayId: number;
  editing: boolean;
  timeZoneAbbr: string | undefined;
  onPastMidnightClick?: (dayId: number, index: number) => void;
}

type AvailabilitySlot = CoachAvailabilityFormState['availabilitiesByDay'][number]['availabilities'][number];

interface WeeklyHoursWindowProps {
  form: UseFormReturn<CoachAvailabilityFormState>;
  day: DayOfWeek;
  dayId: number;
  index: number;
  editing: boolean;
  timeZoneAbbr: string | undefined;
  fieldValue: AvailabilitySlot | undefined;
  defaultPlainTime: Temporal.PlainTime;
  onRemove: () => void;
  onPastMidnightClick?: (dayId: number, index: number) => void;
  mobileAddButton: ReactNode;
}

// Start Time / End Time availability window
// Desktop: Two dropdown time pickers
// Mobile & Small Screen:  Button that opens a bottom-sheet modal for both
function WeeklyHoursWindow({
  form,
  day,
  dayId,
  index,
  editing,
  timeZoneAbbr,
  fieldValue,
  defaultPlainTime,
  onRemove,
  onPastMidnightClick,
  mobileAddButton,
}: WeeklyHoursWindowProps) {
  const [pickerOpen, setPickerOpen] = useState(false);

  const startTime = fieldValue?.startPlainTime ?? defaultPlainTime;
  const endTime = fieldValue?.endPlainTime ?? defaultPlainTime;
  const start = formatPlainTime(startTime, { hour12: true });
  const end = formatPlainTime(endTime, { hour12: true });

  return (
    <InfoKeyValue
      label=""
      value={
        <div className="w-40 text-sm flex items-center justify-between">
          <span className="whitespace-nowrap">
            {start} - {end}
          </span>
          <span className="ml-2">{timeZoneAbbr}</span>
        </div>
      }
      data-testid={`my-availability-weekly-hours-availability-display-${day.toLowerCase()}-${index}`}
      edit={{
        editing,
        editModeComponent: (
          <div className="flex flex-row flex-start items-center gap-1 md:gap-2">
            {/* Desktop Time Range Selectors */}
            <div className="flex touch-mobile:hidden flex-row items-center gap-2">
              <FormInputTime
                form={form}
                name={`availabilitiesByDay.${dayId}.availabilities.${index}.startPlainTime`}
                shouldDisplayError={false}
                shouldDisplayIcon={false}
                triggerButtonProps={{
                  'data-testid': `my-availability-weekly-hours-input-start-${day.toLowerCase()}-${index}`,
                  className: 'w-[80px] lg:w-[125px] px-2 lg:px-4',
                }}
              />
              <div className="mx-0 md:mx-2">-</div>
              <FormInputTime
                form={form}
                name={`availabilitiesByDay.${dayId}.availabilities.${index}.endPlainTime`}
                min={startTime}
                shouldDisplayError={false}
                shouldDisplayIcon={false}
                triggerButtonProps={{
                  'data-testid': `my-availability-weekly-hours-input-end-${day.toLowerCase()}-${index}`,
                  className: 'w-[80px] lg:w-[125px] px-2 lg:px-4',
                }}
                footerAction={{
                  label: 'Need to schedule past midnight?',
                  onClick: ({ closePopover }) => {
                    closePopover();
                    setTimeout(() => onPastMidnightClick?.(dayId, index), 100);
                  },
                }}
              />
            </div>

            {/* Mobile Time Range Selector */}
            <div className="hidden touch-mobile:flex">
              <Button
                variant="outline"
                type="button"
                className="w-[150px] px-2 font-normal justify-center"
                onClick={() => setPickerOpen(true)}
                data-testid={`my-availability-weekly-hours-input-range-${day.toLowerCase()}-${index}`}
              >
                {start} - {end}
              </Button>
              <FormInputMobileTimeRangeSheet
                open={pickerOpen}
                onOpenChange={setPickerOpen}
                start={startTime}
                end={endTime}
                onSave={(nextStart, nextEnd) => {
                  form.setValue(`availabilitiesByDay.${dayId}.availabilities.${index}.startPlainTime`, nextStart, {
                    shouldDirty: true,
                  });
                  form.setValue(`availabilitiesByDay.${dayId}.availabilities.${index}.endPlainTime`, nextEnd, {
                    shouldDirty: true,
                  });
                }}
                data-testid={`my-availability-weekly-hours-range-sheet-${day.toLowerCase()}-${index}`}
              />
            </div>

            <span>{timeZoneAbbr}</span>
            <div>
              <Button
                variant="ghost"
                type="button"
                size="icon"
                title="Remove availability"
                icon={CircleX}
                iconClassName="w-6 h-6"
                onClick={onRemove}
                data-testid={`my-availability-weekly-hours-button-remove-${day.toLowerCase()}-${index}`}
              />
              {mobileAddButton}
            </div>
          </div>
        ),
      }}
    />
  );
}

function AddTimeSlotButton({
  editing,
  day,
  weekDayLabel,
  defaultPlainTime,
  append,
}: {
  editing: boolean;
  day: DayOfWeek;
  weekDayLabel: string;
  defaultPlainTime: Temporal.PlainTime;
  append: (value: { startPlainTime: Temporal.PlainTime; endPlainTime: Temporal.PlainTime }) => void;
}) {
  if (!editing) return null;
  return (
    <Button
      variant="ghost"
      type="button"
      size="icon"
      className="text-foreground"
      title={`Add availability for ${weekDayLabel}`}
      icon={CirclePlus}
      iconClassName="w-6 h-6"
      onClick={() =>
        append({
          startPlainTime: defaultPlainTime,
          endPlainTime: defaultPlainTime,
        })
      }
      data-testid={`my-availability-weekly-hours-button-add-${day.toLowerCase()}`}
    />
  );
}

export function WeeklyHoursFormRow({
  form,
  day,
  dayId,
  editing,
  timeZoneAbbr,
  onPastMidnightClick,
}: WeeklyHoursRowProps) {
  const { control } = form;

  const { fields, append, remove } = useFieldArray({
    control,
    name: `availabilitiesByDay.${dayId}.availabilities`,
  });

  const watchFields = useWatch({
    control,
    name: `availabilitiesByDay.${dayId}.availabilities`,
  });

  const defaultHour = '00:00:00';
  const defaultPlainTime = Temporal.PlainTime.from(defaultHour);
  const weekDayLabel = DayOfWeekLabelMap[day].full;

  // Ensure that the end time is always after the start time
  useEffect(() => {
    const availabilities = watchFields ?? [];
    availabilities.forEach((slot, index) => {
      const start = slot?.startPlainTime;
      const end = slot?.endPlainTime;
      if (start && end && Temporal.PlainTime.compare(end, start) < 0) {
        form.setValue(`availabilitiesByDay.${dayId}.availabilities.${index}.endPlainTime`, start, {
          shouldDirty: true,
        });
      }
    });
  }, [watchFields, dayId, form]);

  return (
    <div className={`flex ${editing ? 'flex-col gap-4 md:flex-row md:gap-0 md:items-start' : 'items-start'}`} key={day}>
      <div
        className="w-[120px] h-10 rounded-lg bg-secondary text-air-primary flex items-center justify-center font-medium"
        title={weekDayLabel}
        data-testid={`my-availability-weekly-hours-day-label-${day.toLowerCase()}`}
      >
        {weekDayLabel}
      </div>
      {fields.length === 0 && (
        <div
          className={`flex space-between items-center text-gray-500 text-sm mt-2 ${editing ? 'ml-3' : 'ml-4'}`}
          data-testid={`my-availability-weekly-hours-day-unavailable-${day.toLowerCase()}`}
        >
          <span>Unavailable</span>
          <span className="md:hidden">
            <AddTimeSlotButton
              editing={editing}
              day={day}
              weekDayLabel={weekDayLabel}
              defaultPlainTime={defaultPlainTime}
              append={append}
            />
          </span>
        </div>
      )}
      <div className={`flex flex-col gap-2 ${editing ? 'md:ml-3' : 'ml-4'}`}>
        {fields.map((field, index) => (
          <div key={field.id} className="flex flex-col gap-2">
            <WeeklyHoursWindow
              form={form}
              day={day}
              dayId={dayId}
              index={index}
              editing={editing}
              timeZoneAbbr={timeZoneAbbr}
              fieldValue={watchFields?.[index]}
              defaultPlainTime={defaultPlainTime}
              onRemove={() => remove(index)}
              onPastMidnightClick={onPastMidnightClick}
              mobileAddButton={
                index === 0 ? (
                  <span className="md:hidden">
                    <AddTimeSlotButton
                      editing={editing}
                      day={day}
                      weekDayLabel={weekDayLabel}
                      defaultPlainTime={defaultPlainTime}
                      append={append}
                    />
                  </span>
                ) : (
                  <span className="inline-flex w-10 md:hidden"></span>
                )
              }
            />
          </div>
        ))}
      </div>
      <div className="hidden md:block">
        <AddTimeSlotButton
          editing={editing}
          day={day}
          weekDayLabel={weekDayLabel}
          defaultPlainTime={defaultPlainTime}
          append={append}
        />
      </div>
    </div>
  );
}
